"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import {
  ArrowRight,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  LayoutGrid,
  ShieldCheck,
  Sparkles,
  Tag,
  User,
  X,
} from "lucide-react";
import { LocaleSwitcher } from "@/components/shared/locale-switcher";
import { brandLogo } from "../../../i18n/config";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { AuditDialog } from "@/components/marketing/audit-dialog";

export function MarketingHeader() {
  const t = useTranslations("marketing.nav");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [bilanOuvert, setBilanOuvert] = useState(false);

  // Les entrées de nav, déclarées une fois : la barre desktop et le tiroir
  // mobile en sont deux rendus, pas deux listes à maintenir.
  // L'icône ne sert que dans le tiroir, où chaque ligne porte sa puce teintée
  // — la reprise du vocabulaire de l'onboarding client.
  const links = [
    { href: `/${locale}/features`, label: t("features"), icon: Sparkles },
    { href: `/${locale}/securite`, label: t("securite"), icon: ShieldCheck },
    { href: `/${locale}/portfolio`, label: t("portfolio"), icon: LayoutGrid },
    { href: `/${locale}/pricing`, label: t("pricing"), icon: Tag },
    { href: `/${locale}/contact`, label: t("contact"), icon: Mail },
  ];

  // Une navigation ferme le tiroir. Sans ça il reste ouvert par-dessus la
  // page d'arrivée — le lien semble n'avoir rien fait.
  // (Échap et le clic extérieur sont gérés par Sheet, via base-ui.)
  useEffect(() => setNavOpen(false), [pathname]);

  // Présence du cookie de session, lue DANS LE NAVIGATEUR : aucun appel réseau,
  // et surtout aucun import de `@supabase/supabase-js` (~45 Ko gzip) sur des
  // pages vitrine qui n'en ont pas d'autre usage. L'ancienne version faisait un
  // `auth.getUser()` — un aller-retour complet, après hydratation, sur la page
  // la plus visitée du site — juste pour choisir la cible d'un lien.
  //
  // Pourquoi pas côté serveur : lire `cookies()` dans le layout marketing le
  // rendrait DYNAMIQUE et annulerait le prérendu de toutes les pages publiques.
  //
  // Ce test n'est PAS un garde d'authentification : il ne décide que de
  // l'affichage du menu. Toute route protégée revalide côté serveur.
  useEffect(() => {
    setIsLoggedIn(/(^|;\s*)sb-[^=]+-auth-token(\.\d+)?=/.test(document.cookie));
  }, []);

  return (
    /* Collant : la nav reste accrochée en haut au défilement. Fond translucide
       + flou, même recette que la barre collante du guide commercial. z-40 :
       au-dessus du contenu, sous le tiroir mobile (Sheet, z-50). */
    <nav className="sticky top-0 z-40 w-full bg-background/80 px-6 py-4 backdrop-blur-md">
      <div className="max-w-7xl mx-auto flex items-center">
        {/* Connecté, la home redirige vers le dashboard (cf. middleware) :
            le logo porte alors l'échappatoire pour rester sur la vitrine. */}
        <Link
          href={isLoggedIn ? `/${locale}?vitrine=1` : `/${locale}`}
          className="shrink-0"
        >
          <Image
            src={brandLogo(locale)}
            alt="RLCore"
            width={120}
            height={40}
            className="h-7 w-auto md:h-8"
            priority
          />
        </Link>

        {/* Le bilan gratuit, juste après le logo : c'est l'offre d'entrée du
            site, elle doit être atteignable de n'importe quelle page. Visible
            uniquement hors session — un client connecté n'en a pas l'usage.
            Sur mobile elle tient entre le logo et le burger. */}
        {!isLoggedIn && (
          /* `flex-1` : le bouton se centre dans l'espace libre entre le logo et
             son voisin de droite — les liens sur desktop, le burger sur mobile
             — au lieu d'être collé au logo. La nav garde son propre `flex-1`,
             les deux blocs se partagent donc la place à parts égales. */
          <div className="flex flex-1 justify-center px-3">
            <button
              type="button"
              onClick={() => setBilanOuvert(true)}
              data-rl-track="header:bilan_open"
              className="primary-button rl-press inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-semibold lg:px-4 lg:text-sm"
            >
              <ShieldCheck className="size-4" />
              {t("audit")}
            </button>
          </div>
        )}

        {/* Nav desktop à partir de lg (1024) et non md : avec le bouton du
            bilan, les quatre liens + langue + connexion se chevauchaient dès
            768 px (mesuré : 4 px de recouvrement en français, davantage en
            espagnol). Entre 768 et 1023 la barre garde donc logo + bilan +
            burger, et les liens vivent dans le tiroir.
            `flex-1` centre la nav ET pousse les actions à droite — plus bas
            elle disparaît, d'où le `ml-auto` sur les actions. */}
        <div className="hidden lg:flex items-center justify-center gap-6 xl:gap-8 flex-1">
          {links.map((l) => {
            const active = pathname === l.href;
            return (
              <Link
                key={l.href}
                href={l.href}
                aria-current={active ? "page" : undefined}
                className={`nav-link text-sm transition-colors ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2 shrink-0 lg:ml-0 lg:gap-3">
          {/* Sur mobile le sélecteur de langue descend dans le panneau :
              trois contrôles côte à côte sur 375 px, c'est illisible. */}
          <div className="hidden lg:block">
            <LocaleSwitcher />
          </div>
          {isLoggedIn ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button className="primary-button px-4 py-2 rounded-lg text-sm font-medium cursor-pointer" />
                }
              >
                {t("myAccount")}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="min-w-56">
                <DropdownMenuItem
                  onClick={() =>
                    router.push(`/${locale}/dashboard/settings?tab=compte`)
                  }
                >
                  <User className="mr-2 h-4 w-4" />
                  {t("myAccount")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => router.push(`/${locale}/dashboard`)}
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  {t("dashboard")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    // Chargé À LA DEMANDE : la déconnexion est le seul usage de
                    // supabase-js sur la vitrine, et elle n'arrive qu'une fois.
                    const { createClient } = await import("@/lib/supabase/client");
                    const supabase = createClient();
                    await supabase.auth.signOut();
                    router.push(`/${locale}/login`);
                    router.refresh();
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            /* La connexion vit désormais DANS le tiroir sur mobile : elle y a
               toute la largeur, au lieu d'un bouton comprimé à côté du burger.
               Sur desktop elle passe en filet : le bouton plein menthe est
               réservé au bilan, deux boutons pleins se seraient concurrencés. */
            <Link
              href={`/${locale}/login`}
              className="hidden rounded-lg border border-white/[0.12] px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground lg:inline-block"
            >
              {t("login")}
            </Link>
          )}

          {/* Burger — carré de verre, même vocabulaire que le sommaire du
              guide commercial. */}
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label={t("openMenu")}
            aria-expanded={navOpen}
            className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.03] text-foreground transition-colors hover:border-primary/40 lg:hidden"
          >
            <Menu className="size-5" />
          </button>
        </div>
      </div>

      {/* La ligne signature vit DANS la nav : elle reste collée avec elle.
          (Avant, elle était un frère dans le layout et défilait avec la page,
          laissant la barre collante sans bord inférieur.) */}
      <div className="divider absolute inset-x-0 bottom-0" />

      {/* ── Tiroir mobile ──────────────────────────────────────────────
          Sheet (base-ui) : glisse depuis la droite, fond flouté, Échap et
          clic extérieur gérés par le primitif. Les enfants entrent ensuite
          en cascade, comme les étapes de l'onboarding client. */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent
          side="right"
          showCloseButton={false}
          /* Largeur en inline, pas en classe : SheetContent la fixe via
             `data-[side=right]:w-3/4`, un sélecteur d'attribut (0,2,0) que
             `w-[86%]` (0,1,0) ne peut pas battre. Le plafond `sm:max-w-sm`
             du composant s'applique toujours. */
          style={{ width: "86%" }}
          className="rl-drawer-glow gap-0 border-l border-white/10 bg-[#141d1b]/95 p-0 backdrop-blur-xl"
        >
          {/* entête du tiroir */}
          <div
            className="rl-drawer-item relative flex items-center justify-between px-5 pb-4 pt-5"
            style={{ ["--d"]: "40ms" } as React.CSSProperties}
          >
            <Image
              src={brandLogo(locale)}
              alt="RLCore"
              width={110}
              height={37}
              className="h-7 w-auto"
            />
            <button
              type="button"
              onClick={() => setNavOpen(false)}
              aria-label={t("closeMenu")}
              className="flex size-9 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.03] text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* navigation */}
          <nav className="relative flex flex-col gap-1 px-3 py-2">
            {links.map((l, i) => {
              const active = pathname === l.href;
              const Icon = l.icon;
              return (
                <Link
                  key={l.href}
                  href={l.href}
                  aria-current={active ? "page" : undefined}
                  className="rl-drawer-item group flex items-center gap-3 rounded-2xl px-2.5 py-2.5 transition-colors hover:bg-white/[0.05]"
                  style={{ ["--d"]: `${90 + i * 55}ms` } as React.CSSProperties}
                >
                  {/* puce teintée — la formule de .rl-chip (14 %), réécrite
                      ici parce que la classe est scopée à .rlcore-app. */}
                  <span
                    className={`flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                      active
                        ? "bg-primary/[0.14] text-primary"
                        : "bg-white/[0.05] text-muted-foreground group-hover:text-primary"
                    }`}
                  >
                    <Icon className="size-[18px]" />
                  </span>
                  <span
                    className={`flex-1 text-[15px] font-medium ${
                      active ? "text-primary" : "text-foreground"
                    }`}
                  >
                    {l.label}
                  </span>
                  <ArrowRight className="size-4 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                </Link>
              );
            })}
          </nav>

          {/* pied : langue puis compte — la connexion en pleine largeur */}
          <div
            className="rl-drawer-item relative mt-auto space-y-4 border-t border-white/[0.08] px-5 pb-7 pt-4"
            style={{ ["--d"]: "270ms" } as React.CSSProperties}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t("language")}
              </span>
              <LocaleSwitcher />
            </div>

            {isLoggedIn ? (
              <div className="space-y-2">
                <Link
                  href={`/${locale}/dashboard`}
                  className="primary-button flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold"
                >
                  <LayoutDashboard className="size-4" />
                  {t("dashboard")}
                </Link>
                <button
                  type="button"
                  onClick={async () => {
                    const { createClient } = await import("@/lib/supabase/client");
                    await createClient().auth.signOut();
                    setNavOpen(false);
                    router.push(`/${locale}/login`);
                    router.refresh();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/[0.08] px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <LogOut className="size-4" />
                  {t("logout")}
                </button>
              </div>
            ) : (
              <Link
                href={`/${locale}/login`}
                className="primary-button flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold"
              >
                {t("login")}
                <ArrowRight className="size-4" />
              </Link>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AuditDialog open={bilanOuvert} onClose={() => setBilanOuvert(false)} />
    </nav>
  );
}
